import type { User } from 'src/types/user';

type ProfileListener = (profile: User | null) => void;

let cachedAccessToken: string | null = null;
let cachedProfile: User | null = null;
let inFlightProfile: Promise<User> | null = null;
const listeners = new Set<ProfileListener>();

const getTokenKey = (): string => localStorage.getItem('accessToken') ?? '';

const notifyListeners = (profile: User | null): void => {
  listeners.forEach((listener) => listener(profile));
};

export const subscribeUserProfile = (listener: ProfileListener): (() => void) => {
  listeners.add(listener);
  listener(cachedProfile);
  return () => listeners.delete(listener);
};

export const clearUserProfileLoader = (): void => {
  cachedAccessToken = null;
  cachedProfile = null;
  inFlightProfile = null;
  notifyListeners(null);
};

export const invalidateUserProfileLoader = (): void => {
  cachedProfile = null;
  inFlightProfile = null;
  cachedAccessToken = null;
};

/** Single in-flight `/user/profile` request per access token. */
export const ensureUserProfileLoaded = (
  fetchProfile: () => Promise<User>
): Promise<User> => {
  const tokenKey = getTokenKey();

  if (!tokenKey) {
    return Promise.reject(new Error('Not authenticated'));
  }

  if (cachedProfile && cachedAccessToken === tokenKey) {
    return Promise.resolve(cachedProfile);
  }

  if (inFlightProfile !== null && cachedAccessToken === tokenKey) {
    return inFlightProfile;
  }

  cachedAccessToken = tokenKey;
  inFlightProfile = fetchProfile()
    .then((profile) => {
      cachedProfile = profile;
      notifyListeners(profile);
      return profile;
    })
    .catch((error) => {
      if (cachedAccessToken === tokenKey) {
        cachedProfile = null;
      }
      notifyListeners(null);
      throw error;
    })
    .finally(() => {
      if (cachedAccessToken === tokenKey) {
        inFlightProfile = null;
      }
    });

  return inFlightProfile;
};
